import { createContext, useContext, useEffect, useState } from 'react';
import getUsuarioPermisos from '../services/permisos/getUsuarioPermisos';

const PermisosContext = createContext(null);

export const PermisosProvider = ({ children }) => {
  const [permisos, setPermisos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const obtenerPermisos = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getUsuarioPermisos();
      
      if (response?.status === 401) {
        setError('No autenticado');
        setPermisos(null);
        return;
      }

      if (response?.data?.success && response?.data?.data) {
        setPermisos(response.data.data);
      } else {
        setError('Error al obtener permisos');
        setPermisos(null);
      }
    } catch (err) {
      console.error('Error en obtenerPermisos:', err);
      setError(err.message || 'Error al obtener permisos');
      setPermisos(null);
    } finally {
      setLoading(false);
    }
  };

  const tienePermiso = (viewNombre, actionNombre = 'ver') => {
    if (!permisos || !permisos.permisos) {
      return false;
    }

    if (!viewNombre) {
      return false;
    }

    const vista = permisos.permisos[viewNombre];
    if (!vista) {
      return false;
    }

    return vista.acciones && vista.acciones.includes(actionNombre);
  };

  const puedeVerRuta = (ruta) => {
    if (ruta === '/' || ruta === '/index') {
      return true;
    }

    if (!permisos || !permisos.permisos) {
      return false;
    }

    const rutaNormalizada = ruta.startsWith('/') ? ruta : `/${ruta}`;

    // Buscar si alguna vista tiene esta URL y el usuario tiene permiso de ver
    for (const viewNombre in permisos.permisos) {
      const vista = permisos.permisos[viewNombre];
      if (vista.url === rutaNormalizada) {
        return tienePermiso(viewNombre, 'ver');
      }
    }

    return false;
  };

  const obtenerNombreVistaPorUrl = (url) => {
    if (!permisos || !permisos.permisos) {
      return null;
    }

    const rutaNormalizada = url.startsWith('/') ? url : `/${url}`;

    for (const viewNombre in permisos.permisos) {
      const vista = permisos.permisos[viewNombre];
      if (vista.url === rutaNormalizada) {
        return viewNombre;
      }
    }

    return null;
  };

  const tienePermisoPorUrl = (url, actionNombre = 'ver') => {
    const viewNombre = obtenerNombreVistaPorUrl(url);
    if (!viewNombre) {
      return false;
    }
    return tienePermiso(viewNombre, actionNombre);
  };

  useEffect(() => {
    obtenerPermisos();
  }, []);

  return (
    <PermisosContext.Provider
      value={{
        permisos,
        loading,
        error,
        tienePermiso,
        puedeVerRuta,
        obtenerNombreVistaPorUrl,
        tienePermisoPorUrl,
        refrescarPermisos: obtenerPermisos,
      }}
    >
      {children}
    </PermisosContext.Provider>
  );
};

export const usePermisos = () => {
  const context = useContext(PermisosContext);
  if (!context) {
    throw new Error('usePermisos debe ser usado dentro de PermisosProvider');
  }
  return context;
};
